/**
 * POS Customer Lookup Component
 * Search customers by phone or email
 * Shows Coffee Pass status with redemption options
 * Shows pending pickup orders for the customer
 * Shows Referral Partner info with balance and payout collection
 */
import { useState, useEffect } from 'react';
import { 
  User, Phone, Mail, Search, ChevronRight, Loader2, 
  Crown, Star, Gift, UserPlus, Package, Clock, Coffee,
  Check, X, ArrowUp, AlertCircle, CreditCard, Banknote, Smartphone,
  Car, Bus, Building, DollarSign, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

export default function POSCustomerLookup({ onSelectCustomer, onNewCustomer, onSkip, staffId, staffName, parkedSalesCount = 0, onShowParkedSales }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [pendingOrders, setPendingOrders] = useState({});
  const [coffeePassStatus, setCoffeePassStatus] = useState({});
  const [upgradeEligibility, setUpgradeEligibility] = useState({});
  const [upgrading, setUpgrading] = useState(null); // Track which customer is being upgraded
  
  // Partner info state
  const [partnerInfo, setPartnerInfo] = useState({});
  
  // NEW: Upgrade payment modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeCustomer, setUpgradeCustomer] = useState(null);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [upgradePaymentMethod, setUpgradePaymentMethod] = useState('cash');
  const [upgradeCashTendered, setUpgradeCashTendered] = useState('');
  const [upgradeMpaisaRef, setUpgradeMpaisaRef] = useState('');
  const [processingUpgrade, setProcessingUpgrade] = useState(false);
  
  // Partner payout modal state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutCustomer, setPayoutCustomer] = useState(null);
  const [payoutData, setPayoutData] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(false);

  // Fetch partner info for a customer
  const fetchPartnerInfo = async (customer) => {
    if (!customer.is_referral_partner) return;
    try {
      const res = await api.get(`/referral-partners/pos/partner-info/${customer.contact_id}`);
      if (res.data.is_partner) {
        setPartnerInfo(prev => ({
          ...prev,
          [customer.contact_id]: res.data
        }));
      }
    } catch (err) {
      console.error('Failed to fetch partner info:', err);
    }
  };

  // Fetch pending orders for a customer
  const fetchPendingOrders = async (customer) => {
    try {
      const queryParam = customer.contact_id 
        ? `customer_id=${customer.contact_id}` 
        : customer.email 
        ? `email=${encodeURIComponent(customer.email)}`
        : customer.phone 
        ? `phone=${encodeURIComponent(customer.phone)}`
        : null;
      
      if (!queryParam) return;
      
      const authParam = staffId ? `&staff_id=${staffId}` : '';
      
      const res = await api.get(`/pos/customer-pending-orders?${queryParam}${authParam}`);
      if (res.data.orders && res.data.orders.length > 0) {
        setPendingOrders(prev => ({
          ...prev,
          [customer.contact_id || customer.email]: res.data.orders
        }));
      }
    } catch (err) {
      console.error('Failed to fetch pending orders:', err);
    }
  };

  // Fetch Coffee Pass status for a customer
  const fetchCoffeePassStatus = async (customer) => {
    try {
      const params = new URLSearchParams();
      if (customer.email) params.append('email', customer.email);
      if (customer.phone) params.append('phone', customer.phone);
      if (customer.contact_id) params.append('contact_id', customer.contact_id);
      
      const res = await api.get(`/coffee-pass/check?${params.toString()}`);
      if (res.data) {
        setCoffeePassStatus(prev => ({
          ...prev,
          [customer.contact_id || customer.email]: res.data
        }));
        
        // If customer has a pass, also check upgrade eligibility
        if (res.data.has_active_pass) {
          fetchUpgradeEligibility(customer);
        }
      }
    } catch (err) {
      console.error('Failed to fetch coffee pass status:', err);
    }
  };

  // Fetch upgrade eligibility for customers with standard passes
  const fetchUpgradeEligibility = async (customer) => {
    try {
      const params = new URLSearchParams();
      if (customer.email) params.append('email', customer.email);
      if (customer.phone) params.append('phone', customer.phone);
      
      const res = await api.get(`/coffee-club/upgrade-eligibility?${params.toString()}`);
      if (res.data) {
        setUpgradeEligibility(prev => ({
          ...prev,
          [customer.contact_id || customer.email]: res.data
        }));
      }
    } catch (err) {
      console.error('Failed to fetch upgrade eligibility:', err);
    }
  };

  // Handle $40 upgrade to extended hours - Opens payment modal
  const handleUpgradePass = async (customer, eligibility) => {
    if (!eligibility?.can_upgrade || !eligibility?.pass_id) {
      toast.error('Customer is not eligible for upgrade');
      return;
    }
    
    // Open the payment modal instead of immediately upgrading
    setUpgradeCustomer(customer);
    setUpgradeInfo(eligibility);
    setUpgradePaymentMethod('cash');
    setUpgradeCashTendered('');
    setUpgradeMpaisaRef('');
    setShowUpgradeModal(true);
  };
  
  // Process the actual upgrade after payment is collected
  const processUpgradePayment = async () => {
    if (!upgradeInfo?.pass_id || !upgradeCustomer) {
      toast.error('Missing upgrade information');
      return;
    }
    
    const upgradePrice = upgradeInfo.upgrade_price || 40;
    
    // Validate payment details
    if (upgradePaymentMethod === 'cash') {
      const cashAmount = parseFloat(upgradeCashTendered) || 0;
      if (cashAmount < upgradePrice) {
        toast.error(`Cash tendered ($${cashAmount.toFixed(2)}) is less than upgrade price ($${upgradePrice.toFixed(2)})`);
        return;
      }
    } else if (upgradePaymentMethod === 'mpaisa') {
      if (!upgradeMpaisaRef.trim()) {
        toast.error('Please enter the M-PAiSA reference code');
        return;
      }
    }
    
    setProcessingUpgrade(true);
    
    try {
      const res = await api.post('/coffee-club/upgrade-to-extended', {
        pass_id: upgradeInfo.pass_id,
        payment_method: upgradePaymentMethod,
        mpaisa_ref_code: upgradePaymentMethod === 'mpaisa' ? upgradeMpaisaRef : null,
        staff_id: staffId,
        source: 'pos'
      });
      
      if (res.data.success) {
        const changeAmount = upgradePaymentMethod === 'cash' 
          ? (parseFloat(upgradeCashTendered) || 0) - upgradePrice 
          : 0;
        
        const customerName = upgradeCustomer.first_name || upgradeCustomer.name || 'Customer';
        const customerPhone = upgradeCustomer.phone || '';
        
        // CRITICAL: Print receipt for the upgrade transaction
        try {
          await api.post('/receipts/coffee-pass-upgrade-print', null, {
            params: {
              transaction_id: res.data.transaction_id,
              pass_id: upgradeInfo.pass_id,
              customer_name: customerName,
              customer_phone: customerPhone,
              amount: upgradePrice,
              payment_method: upgradePaymentMethod === 'mpaisa' ? 'mpaisa' : upgradePaymentMethod,
              staff_name: staffName || 'Staff',
              cash_tendered: upgradePaymentMethod === 'cash' ? parseFloat(upgradeCashTendered) || 0 : null,
              change_given: upgradePaymentMethod === 'cash' ? changeAmount : null,
              mpaisa_ref: upgradePaymentMethod === 'mpaisa' ? upgradeMpaisaRef : null
            }
          });
          console.log('Coffee Pass upgrade receipt printed successfully');
        } catch (printErr) {
          console.error('Failed to print upgrade receipt:', printErr);
          // Don't fail the whole operation if print fails
          toast.warning('Upgrade successful but receipt print failed', {
            description: 'Please print a manual receipt'
          });
        }
        
        toast.success('Pass upgraded to Extended Hours!', {
          description: `${customerName} can now redeem 7-10 AM + 1-3:30 PM${changeAmount > 0 ? `. Change: $${changeAmount.toFixed(2)}` : ''}`
        });
        
        // Refresh the pass status and eligibility
        fetchCoffeePassStatus(upgradeCustomer);
        
        // Close the modal
        setShowUpgradeModal(false);
        setUpgradeCustomer(null);
        setUpgradeInfo(null);
      } else {
        toast.error(res.data.error || 'Failed to upgrade pass');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upgrade pass');
    } finally {
      setProcessingUpgrade(false);
    }
  };

  const getCustomerUpgradeEligibility = (customer) => {
    return upgradeEligibility[customer.contact_id] || upgradeEligibility[customer.email] || null;
  };

  const searchCustomers = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setSearched(true);
    setPendingOrders({});
    setCoffeePassStatus({});
    setPartnerInfo({});
    
    try {
      const res = await api.get(`/staff/lookup?query=${encodeURIComponent(query.trim())}`);
      const customers = res.data.contacts || [];
      setResults(customers);
      
      // Fetch details for each customer in parallel
      customers.forEach(customer => {
        fetchPendingOrders(customer);
        fetchCoffeePassStatus(customer);
        fetchPartnerInfo(customer);  // Fetch partner info if they are a partner
      });
    } catch (err) {
      toast.error('Failed to search customers');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerPendingOrders = (customer) => {
    return pendingOrders[customer.contact_id] || pendingOrders[customer.email] || [];
  };

  const getCustomerCoffeePass = (customer) => {
    return coffeePassStatus[customer.contact_id] || coffeePassStatus[customer.email] || null;
  };

  const getCustomerPartnerInfo = (customer) => {
    return partnerInfo[customer.contact_id] || null;
  };

  // Handle partner payout collection
  const handleCollectPayout = async (payout) => {
    setProcessingPayout(true);
    try {
      const res = await api.post('/referral-partners/pos/collect-payout', {
        payout_id: payout.payout_id,
        cashier_id: staffId,
        cashier_name: 'POS Cashier'
      });
      
      if (res.data.success) {
        toast.success(`Payout collected!`, {
          description: `Give ${res.data.partner_name} $${res.data.amount_paid.toFixed(2)} cash`
        });
        // Refresh partner info
        if (payoutCustomer) {
          fetchPartnerInfo(payoutCustomer);
        }
        setShowPayoutModal(false);
        setPayoutData(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  // Handle customer selection with Coffee Pass info
  const handleSelectCustomer = (customer, passStatus) => {
    // Attach coffee pass info to customer object
    const customerWithPass = {
      ...customer,
      coffeePass: passStatus
    };
    onSelectCustomer(customerWithPass);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6" data-testid="pos-customer-lookup">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Lookup</h1>
          <p className="text-gray-500 dark:text-gray-400">Search by phone number or email</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Parked Sales Button */}
          {parkedSalesCount > 0 && onShowParkedSales && (
            <Button
              variant="outline"
              onClick={onShowParkedSales}
              className="relative bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
              data-testid="pos-parked-sales-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Parked Sales
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                {parkedSalesCount}
              </span>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onSkip}
            className="text-gray-600 dark:text-gray-300"
            data-testid="pos-skip-lookup"
          >
            Skip - Guest Order
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto">
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Enter phone number or email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchCustomers()}
              className="pl-12 h-14 text-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              data-testid="pos-customer-search-input"
            />
          </div>
          <Button
            onClick={searchCustomers}
            disabled={loading || !query.trim()}
            className="h-14 px-8 bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="pos-customer-search-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
          </Button>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-3">
            {results.map((customer) => {
              const customerPendingOrders = getCustomerPendingOrders(customer);
              const passStatus = getCustomerCoffeePass(customer);
              const upgradeStatus = getCustomerUpgradeEligibility(customer);
              const partnerData = getCustomerPartnerInfo(customer);
              const PartnerTypeIcon = customer.referral_partner_type === 'taxi' ? Car 
                : customer.referral_partner_type === 'tour_bus' ? Bus 
                : customer.referral_partner_type === 'hotel' ? Building : User;
              
              return (
                <div
                  key={customer.contact_id}
                  className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700"
                >
                  <button
                    onClick={() => handleSelectCustomer(customer, passStatus)}
                    className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all text-left"
                    data-testid={`pos-customer-result-${customer.contact_id}`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      customer.is_referral_partner 
                        ? 'bg-purple-100 dark:bg-purple-900/30' 
                        : 'bg-teal-100 dark:bg-teal-900/30'
                    }`}>
                      {customer.is_referral_partner ? (
                        <PartnerTypeIcon className="w-6 h-6 text-purple-600" />
                      ) : (
                        <User className="w-6 h-6 text-teal-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {customer.first_name || customer.name} {customer.last_name || ''}
                        </h3>
                        {/* Referral Partner Badge */}
                        {customer.is_referral_partner && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> Partner ({customer.referral_partner_code})
                          </span>
                        )}
                        {customer.membership_tier && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.membership_tier === 'Founding Member' 
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : customer.membership_tier === 'Gold'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : customer.membership_tier === 'Silver'
                              ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          }`}>
                            {customer.membership_tier === 'Founding Member' ? (
                              <span className="flex items-center gap-1"><Crown className="w-3 h-3" /> Founding</span>
                            ) : (
                              <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {customer.membership_tier}</span>
                            )}
                          </span>
                        )}
                        {(customer.has_active_coffee_pass || passStatus?.has_active_pass) && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
                            <Coffee className="w-3 h-3" /> Bula Pass
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </span>
                        )}
                        {customer.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {customer.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                  
                  {/* BULA COFFEE PASS STATUS - Prominent Display */}
                  {passStatus?.has_active_pass && (
                    <div className={`border-t p-4 ${
                      passStatus.can_redeem_now 
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                        : passStatus.already_redeemed_today
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          passStatus.can_redeem_now 
                            ? 'bg-green-500' 
                            : passStatus.already_redeemed_today
                            ? 'bg-amber-500'
                            : 'bg-blue-500'
                        }`}>
                          <Coffee className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className={`font-bold ${
                              passStatus.can_redeem_now 
                                ? 'text-green-700 dark:text-green-300' 
                                : passStatus.already_redeemed_today
                                ? 'text-amber-700 dark:text-amber-300'
                                : 'text-blue-700 dark:text-blue-300'
                            }`}>
                              BULA COFFEE PASS
                            </h4>
                            {/* Plan Type Badge */}
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                              passStatus.plan_type === 'extended' || passStatus.plan_type === 'upgraded'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {passStatus.plan_type === 'extended' ? 'EXTENDED' : 
                               passStatus.plan_type === 'upgraded' ? 'UPGRADED' : 'STANDARD'}
                            </span>
                            {passStatus.can_redeem_now ? (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full animate-pulse">
                                <Check className="w-3 h-3" /> READY TO REDEEM
                              </span>
                            ) : passStatus.already_redeemed_today ? (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
                                <Check className="w-3 h-3" /> REDEEMED TODAY
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full">
                                <Clock className="w-3 h-3" /> OUTSIDE HOURS
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {passStatus.message}
                          </p>
                          
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                            <span>Total Redemptions: <strong className="text-gray-700 dark:text-gray-300">{passStatus.total_redemptions || 0}</strong></span>
                            <span>•</span>
                            <span>Window: <strong className="text-gray-700 dark:text-gray-300">
                              {passStatus.plan_type === 'extended' || passStatus.plan_type === 'upgraded' 
                                ? '7-10 AM + 1-3:30 PM' 
                                : '7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM'}
                            </strong></span>
                          </div>
                          
                          {passStatus.can_redeem_now && (
                            <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700">
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Gift className="w-4 h-4 text-green-500" />
                                Free 8oz coffee • Upgrade to 12oz for $3
                              </p>
                            </div>
                          )}
                          
                          {/* $40 UPGRADE BUTTON - Only for standard pass holders */}
                          {upgradeStatus?.can_upgrade && (
                            <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-sm font-bold text-purple-700 dark:text-purple-300 flex items-center gap-2">
                                    <ArrowUp className="w-4 h-4" />
                                    Upgrade to Extended Hours
                                  </p>
                                  <p className="text-xs text-purple-600 dark:text-purple-400">
                                    Add afternoon redemption (1-3:30 PM)
                                  </p>
                                </div>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpgradePass(customer, upgradeStatus);
                                  }}
                                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                                  data-testid={`pos-upgrade-pass-${customer.contact_id}`}
                                >
                                  ${upgradeStatus.upgrade_price || 40} Upgrade
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending Pickup Orders */}
                  {customerPendingOrders.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                          {customerPendingOrders.length} Order{customerPendingOrders.length > 1 ? 's' : ''} Waiting for Pickup
                        </span>
                      </div>
                      <div className="space-y-2">
                        {customerPendingOrders.slice(0, 3).map((order) => (
                          <div 
                            key={order.order_id}
                            className="flex items-center justify-between text-sm bg-white dark:bg-gray-800 rounded-lg p-2"
                          >
                            <div>
                              <span className="font-bold text-gray-900 dark:text-white">#{order.order_number}</span>
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${
                                order.status === 'ready' || order.status === 'ready_for_pickup'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}>
                                {order.status === 'ready' || order.status === 'ready_for_pickup' ? 'READY' : 'Preparing'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                              <Clock className="w-3 h-3" />
                              <span>{order.item_count} items</span>
                              <span className="font-medium text-gray-900 dark:text-white">${order.total?.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* REFERRAL PARTNER INFO - Show balance and payout options */}
                  {customer.is_referral_partner && partnerData && (
                    <div className="border-t p-4 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h4 className="font-bold text-purple-700 dark:text-purple-300">
                              REFERRAL PARTNER
                            </h4>
                            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-purple-200 text-purple-700 dark:bg-purple-800 dark:text-purple-300">
                              {customer.referral_partner_code}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
                              {customer.referral_partner_type?.replace('_', ' ')}
                            </span>
                          </div>
                          
                          {/* Balance Display */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Available Balance</p>
                              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                ${partnerData.partner?.commission_balance?.toFixed(2) || '0.00'}
                              </p>
                              {partnerData.can_use_balance && (
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">Can use for purchases</p>
                              )}
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Total Earned</p>
                              <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                                ${partnerData.partner?.total_commission_earned?.toFixed(2) || '0.00'}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">{partnerData.partner?.total_referral_sales || 0} referrals</p>
                            </div>
                          </div>
                          
                          {/* Approved Payouts Ready for Collection */}
                          {partnerData.approved_payouts?.length > 0 && (
                            <div className="mb-3 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border-2 border-green-400 dark:border-green-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Banknote className="w-5 h-5 text-green-600" />
                                <span className="font-bold text-green-700 dark:text-green-300">
                                  PAYOUT APPROVED - READY FOR COLLECTION
                                </span>
                              </div>
                              {partnerData.approved_payouts.map((payout) => (
                                <div key={payout.payout_id} className="flex items-center justify-between mt-2">
                                  <div>
                                    <span className="font-bold text-lg text-green-700 dark:text-green-300">
                                      ${payout.amount_approved?.toFixed(2) || payout.amount_requested?.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                                      Cash payout
                                    </span>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPayoutCustomer(customer);
                                      setPayoutData(payout);
                                      setShowPayoutModal(true);
                                    }}
                                    data-testid="collect-payout-btn"
                                  >
                                    <Banknote className="w-4 h-4 mr-1" />
                                    Give Cash & Mark Collected
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Use Balance for Purchase Info */}
                          {partnerData.can_use_balance && !partnerData.approved_payouts?.length && (
                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <DollarSign className="w-4 h-4 inline mr-1 text-purple-500" />
                                Partner can use balance as payment at checkout.
                                Min <strong>${partnerData.min_cash_payout}</strong> for cash payout.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : searched ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No customer found</p>
            <Button
              onClick={onNewCustomer}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="pos-new-customer-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create New Customer
            </Button>
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Search for a customer or</p>
            <Button
              onClick={onNewCustomer}
              variant="outline"
              className="mt-4"
              data-testid="pos-new-customer-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create New Customer
            </Button>
          </div>
        )}
      </div>
      
      {/* UPGRADE PAYMENT MODAL */}
      {showUpgradeModal && upgradeCustomer && upgradeInfo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="upgrade-payment-modal">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <ArrowUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Upgrade Coffee Pass</h3>
                    <p className="text-purple-200 text-sm">{upgradeCustomer.first_name} {upgradeCustomer.last_name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Upgrade Details */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-purple-700 dark:text-purple-300 font-medium">Extended Hours Upgrade</span>
                  <span className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                    ${(upgradeInfo.upgrade_price || 40).toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-purple-600 dark:text-purple-400">
                  Adds afternoon redemption window: 1:00 PM - 3:30 PM
                </p>
              </div>
              
              {/* Payment Method Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                  Payment Method
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setUpgradePaymentMethod('cash')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                      upgradePaymentMethod === 'cash'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                    data-testid="upgrade-pay-cash"
                  >
                    <Banknote className={`w-8 h-8 ${upgradePaymentMethod === 'cash' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`font-medium ${upgradePaymentMethod === 'cash' ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      Cash
                    </span>
                  </button>
                  <button
                    onClick={() => setUpgradePaymentMethod('mpaisa')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                      upgradePaymentMethod === 'mpaisa'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                    data-testid="upgrade-pay-mpaisa"
                  >
                    <Smartphone className={`w-8 h-8 ${upgradePaymentMethod === 'mpaisa' ? 'text-red-600' : 'text-gray-400'}`} />
                    <span className={`font-medium ${upgradePaymentMethod === 'mpaisa' ? 'text-red-700 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      M-PAiSA
                    </span>
                  </button>
                </div>
              </div>
              
              {/* Payment Details */}
              {upgradePaymentMethod === 'cash' ? (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                    Cash Tendered
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={upgradeCashTendered}
                      onChange={(e) => setUpgradeCashTendered(e.target.value)}
                      className="pl-8 text-2xl font-bold h-14"
                      placeholder="0.00"
                      data-testid="upgrade-cash-input"
                    />
                  </div>
                  {/* Quick Cash Buttons */}
                  <div className="grid grid-cols-4 gap-2">
                    {[40, 50, 60, 100].map(amount => (
                      <button
                        key={amount}
                        onClick={() => setUpgradeCashTendered(amount.toString())}
                        className="py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-gray-700 dark:text-gray-300 transition-colors"
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  {/* Change Calculation */}
                  {parseFloat(upgradeCashTendered) >= (upgradeInfo.upgrade_price || 40) && (
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-green-700 dark:text-green-400 font-medium">Change Due:</span>
                      <span className="text-xl font-bold text-green-700 dark:text-green-400">
                        ${(parseFloat(upgradeCashTendered) - (upgradeInfo.upgrade_price || 40)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                    M-PAiSA Reference Code
                  </label>
                  <Input
                    type="text"
                    value={upgradeMpaisaRef}
                    onChange={(e) => setUpgradeMpaisaRef(e.target.value.toUpperCase())}
                    className="text-lg font-mono h-14"
                    placeholder="e.g. MP123456"
                    data-testid="upgrade-mpaisa-ref-input"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enter the reference code from the customer&apos;s M-PAiSA payment confirmation
                  </p>
                </div>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <Button
                onClick={() => setShowUpgradeModal(false)}
                variant="outline"
                className="flex-1"
                disabled={processingUpgrade}
              >
                Cancel
              </Button>
              <Button
                onClick={processUpgradePayment}
                disabled={processingUpgrade || (upgradePaymentMethod === 'cash' && (!upgradeCashTendered || parseFloat(upgradeCashTendered) < (upgradeInfo.upgrade_price || 40))) || (upgradePaymentMethod === 'mpaisa' && !upgradeMpaisaRef.trim())}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="upgrade-confirm-payment-btn"
              >
                {processingUpgrade ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Confirm ${(upgradeInfo.upgrade_price || 40).toFixed(2)} Payment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* PARTNER PAYOUT COLLECTION MODAL */}
      {showPayoutModal && payoutData && payoutCustomer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-green-600 p-6 text-white">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Banknote className="w-6 h-6" />
                Cash Payout Collection
              </h3>
              <p className="text-green-100 mt-1 text-sm">
                Confirm you are giving cash to this partner
              </p>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <User className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900 dark:text-white">
                    {payoutCustomer.name || `${payoutCustomer.first_name} ${payoutCustomer.last_name}`}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {payoutCustomer.phone} • {payoutCustomer.referral_partner_code}
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 text-center border-2 border-green-200 dark:border-green-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Amount to Give in Cash</p>
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">
                  ${payoutData.amount_approved?.toFixed(2) || payoutData.amount_requested?.toFixed(2)}
                </p>
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Important:</strong> Only click confirm AFTER you have given the cash to the partner. 
                    This will mark the payout as collected and cannot be undone.
                  </span>
                </p>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <Button
                onClick={() => {
                  setShowPayoutModal(false);
                  setPayoutData(null);
                  setPayoutCustomer(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={processingPayout}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleCollectPayout(payoutData)}
                disabled={processingPayout}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-payout-btn"
              >
                {processingPayout ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Confirm Cash Given
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
